import { mapTo } from 'rxjs/operators/mapTo';
import { map } from 'rxjs/operators/map';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { filter } from 'rxjs/operators/filter';
import { tap } from 'rxjs/operators/tap';
import get from 'lodash.get';

import cookie from 'js-cookie';
import { combineEpics } from 'redux-observable';

import { setRoute, setUrl } from '../actions/routes';

import {
  whenError,
  whileOnRoute,
  whenLoggedInCustomerIsInCohort,
} from './helpers/operators';

import {
  CART_NOT_ACTIVE,
  CART_NOT_FOUND,
  DOWNLOAD_CART_NO_SHIPPING,
} from '../constants/errors';

import CHECKOUT_STEPS_ROUTES from '../routes/checkout';
import { LEGACY_CART } from '../constants/urls';

import { BOX_ONLY, MIXED } from '../constants/cartTypes';
import AddressStepGuardQuery from '../graphql/AddressStepGuardQuery';
import DeliveryStepGuardQuery from '../graphql/DeliveryStepGuardQuery';

// Cleans the cart id from client session and redirects to the (empty)
// cart page if a cart_not_active error is encountered
const redirectIfNotActive = (action$) =>
  action$.pipe(
    whenError(CART_NOT_ACTIVE, CART_NOT_FOUND),
    whileOnRoute(
      action$,
      CHECKOUT_STEPS_ROUTES.address.path,
      CHECKOUT_STEPS_ROUTES.delivery.path,
      CHECKOUT_STEPS_ROUTES.payment.path,
      CHECKOUT_STEPS_ROUTES.review.path
    ),
    tap(() => cookie.remove('userquote')),
    map(() => setUrl(LEGACY_CART))
  );

const redirectFromShippingToAddressIfDownloadOnly = (action$) =>
  action$.pipe(
    whenError(DOWNLOAD_CART_NO_SHIPPING),
    whileOnRoute(action$, CHECKOUT_STEPS_ROUTES.delivery.path),
    mapTo(setRoute(CHECKOUT_STEPS_ROUTES.address.path))
  );

const redirectToAddressIfAddressIsMissing = (
  action$,
  state,
  { apolloClient }
) =>
  action$.pipe(
    whileOnRoute(
      action$,
      CHECKOUT_STEPS_ROUTES.delivery.path,
      CHECKOUT_STEPS_ROUTES.payment.path,
      CHECKOUT_STEPS_ROUTES.review.path
    ),
    whenLoggedInCustomerIsInCohort(apolloClient),
    mergeMap(() =>
      apolloClient.query({
        query: AddressStepGuardQuery,
      })
    ),
    filter(({ data }) => {
      const shippingId = get(
        data,
        'user.cart.shippingAddress.customerAddress.id'
      );
      const billingId = get(
        data,
        'user.cart.billingAddress.customerAddress.id'
      );
      const type = get(data, 'user.cart.type');

      return !billingId || ([BOX_ONLY, MIXED].includes(type) && !shippingId);
    }),
    mapTo(setRoute(CHECKOUT_STEPS_ROUTES.address.path))
  );

const redirectToDeliveryIfDeliveryIsMissing = (
  action$,
  state,
  { apolloClient }
) =>
  action$.pipe(
    whileOnRoute(
      action$,
      CHECKOUT_STEPS_ROUTES.payment.path,
      CHECKOUT_STEPS_ROUTES.review.path
    ),
    whenLoggedInCustomerIsInCohort(apolloClient),
    mergeMap(() =>
      apolloClient.query({
        query: DeliveryStepGuardQuery,
      })
    ),
    filter(({ data }) => {
      const shippingMethodId = get(data, 'user.cart.shippingMethod.id');
      const type = get(data, 'user.cart.type');
      return [BOX_ONLY, MIXED].includes(type) && !shippingMethodId;
    }),
    mapTo(setRoute(CHECKOUT_STEPS_ROUTES.delivery.path))
  );

const cartEpic = combineEpics(
  redirectIfNotActive,
  redirectFromShippingToAddressIfDownloadOnly,
  redirectToAddressIfAddressIsMissing,
  redirectToDeliveryIfDeliveryIsMissing
);

export default cartEpic;
